import { Typography } from "@mui/material";
import { Splitter, SplitterOnChangeEvent } from "@progress/kendo-react-layout";
import { debounce } from "lodash";
import monaco from "monaco-editor";
import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "store/hooks";
import useValidationDedicatedWorker from "../hooks/useDedicatedWorker";
import {
  setInnerPanes,
  setIsErrorInLayoutEditor,
  setIsErrorInOptionsEditor,
  setIsErrorInSchemaEditor,
  setPanes,
} from "../store/codeEditor";
import {
  formDefinitionLayoutChanged,
  formDefinitionOptionsChanged,
  formDefinitionSchemaChanged,
} from "../store/editorSlice";
import { EditorVariant } from "../types";
import ExpressionEditorDialog from "./ExpressionEditorDialog";
import EditorLabel from "./editorLabel";
import FormEditor from "./formEditor";
import FormPreview from "./formPreview";
import { FORM_DEFINITION_LAYOUT_MODEL_URI } from "./jsonSchema/formDefinitionLayoutJsonSchema";
import { FORM_DEFINITION_OPTIONS_MODEL_URI } from "./jsonSchema/formDefinitionOptionsJsonSchema";
import { FORM_DEFINITION_SCHEMA_MODEL_URI } from "./jsonSchema/formDefinitionSchemaJsonSchema";

interface IFormCodeEditor {}

const CodeFormEditor = (props: IFormCodeEditor) => {
  const dispatch = useAppDispatch();

  const { panes, innerPanes } = useAppSelector(
    (state) => state.developer.edit.codeEditor
  );
  const schemaObject = useAppSelector(
    (x) => x.developer.edit.editor.schemaObject
  );
  const isInvalidJsonSchema = useAppSelector(
    (x) => x.developer.edit.editor.isInvalidJsonSchema
  );
  const isInvalidJsonLayout = useAppSelector(
    (x) => x.developer.edit.editor.isInvalidJsonLayout
  );
  const isInvalidJsonOptions = useAppSelector(
    (x) => x.developer.edit.editor.isInvalidJsonOptions
  );

  const { schemaJsonEdited, layoutJsonEdited, optionsJsonEdited } =
    useAppSelector((state) => state.developer.edit.editor);

  const validateSchema = useValidationDedicatedWorker(EditorVariant.Schema);
  const validateLayout = useValidationDedicatedWorker(EditorVariant.Layout);

  const layoutValidations = useCallback(
    (jsonObject: any, jsonLines: string[]) => {
      validateLayout?.current?.(
        schemaObject,
        JSON.parse(schemaJsonEdited),
        jsonObject,
        jsonLines
      );
    },
    [schemaObject, schemaJsonEdited, validateLayout]
  );

  const schemaValidations = useCallback(
    (jsonObject: any, jsonLines: string[]) => {
      validateSchema?.current?.(schemaObject, null, jsonObject, jsonLines);
    },
    [schemaObject, validateSchema]
  );

  const handleChangePanes = (event: SplitterOnChangeEvent) => {
    dispatch(setPanes(event.newState));
  };

  const handleChangeInnerPanes = (event: SplitterOnChangeEvent) => {
    dispatch(setInnerPanes(event.newState));
  };

  const handleSchemaEditorValidation = useCallback(
    (markers: monaco.editor.IMarker[]) => {
      dispatch(setIsErrorInSchemaEditor(markers.length !== 0));
    },
    [dispatch]
  );

  const handleLayoutEditorValidation = useCallback(
    (markers: monaco.editor.IMarker[]) => {
      dispatch(setIsErrorInLayoutEditor(markers.length !== 0));
    },
    [dispatch]
  );

  const handleOptionsEditorValidation = useCallback(
    (markers: monaco.editor.IMarker[]) => {
      dispatch(setIsErrorInOptionsEditor(markers.length !== 0));
    },
    [dispatch]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSchemaEditorChange = useCallback(
    debounce((value: string | undefined) => {
      if (value === undefined) return;
      dispatch(formDefinitionSchemaChanged(value));
    }, 1000),
    [dispatch]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleLayoutEditorChange = useCallback(
    debounce((value: string | undefined) => {
      if (value === undefined) return;
      dispatch(formDefinitionLayoutChanged(value));
    }, 1000),
    [dispatch]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleOptionsEditorChange = useCallback(
    debounce((value: string | undefined) => {
      if (value === undefined) return;
      dispatch(formDefinitionOptionsChanged(value));
    }, 1000),
    [dispatch]
  );

  const { errorsInLayoutEditor, errorsInSchemaEditor } = useAppSelector(
    (state) => state.developer.edit.codeEditor
  );

  const invalidJson: string[] = [];
  if (isInvalidJsonSchema) invalidJson.push("Schema");
  if (isInvalidJsonLayout) invalidJson.push("Layout");
  if (isInvalidJsonOptions) invalidJson.push("Options");
  const invalidJsonMessage =
    invalidJson.length > 0
      ? `Invalid Json: ${invalidJson.join(", ")}`
      : undefined;

  return (
    <>
      <Splitter
        orientation="vertical"
        style={{ height: "100%" }}
        panes={panes}
        onChange={handleChangePanes}
      >
        <Splitter
          orientation="horizontal"
          style={{ height: "100%" }}
          panes={innerPanes}
          onChange={handleChangeInnerPanes}
        >
          {schemaJsonEdited !== undefined && (
            <div style={{ height: "100%", overflowX: "hidden" }}>
              <FormEditor
                json={schemaJsonEdited}
                modelUri={FORM_DEFINITION_SCHEMA_MODEL_URI}
                onValidate={handleSchemaEditorValidation}
                onChange={handleSchemaEditorChange}
                validateFn={schemaValidations}
                errors={errorsInSchemaEditor}
              />
              <EditorLabel label="schema" />
            </div>
          )}
          {layoutJsonEdited !== undefined && (
            <div style={{ height: "100%" }}>
              <FormEditor
                json={layoutJsonEdited}
                modelUri={FORM_DEFINITION_LAYOUT_MODEL_URI}
                onValidate={handleLayoutEditorValidation}
                onChange={handleLayoutEditorChange}
                validateFn={layoutValidations}
                errors={errorsInLayoutEditor}
              />
              <EditorLabel label="layout" />
            </div>
          )}
          {optionsJsonEdited !== undefined && (
            <div style={{ height: "100%" }}>
              <FormEditor
                json={optionsJsonEdited}
                modelUri={FORM_DEFINITION_OPTIONS_MODEL_URI}
                onValidate={handleOptionsEditorValidation}
                onChange={handleOptionsEditorChange}
              />
              <EditorLabel label="options" />
            </div>
          )}
        </Splitter>
        <div
          style={{
            height: "100%",
            overflowY: "auto",
          }}
        >
          {!!invalidJsonMessage ? (
            <Typography color="error">{invalidJsonMessage}</Typography>
          ) : (
            <FormPreview />
          )}
        </div>
      </Splitter>
      <ExpressionEditorDialog />
    </>
  );
};

export default CodeFormEditor;
